import { useEffect, useRef } from "react";

// Esc для окна, которое живёт в шелле поверх остальных слоёв: окно исходящего
// звонка и вопрос про обратную связь после него.
//
// Слои под таким окном — карточка кандидата, лента — слушают Esc на window
// сами и про окно не знают: раньше оно было их частью и исчезало вместе с
// ними. Теперь карточка закрылась бы ПОД окном, а окно осталось бы висеть над
// списком. Поэтому клавишу забираем в фазе перехвата и дальше не пускаем —
// то же, что useBackClose (mobile/backClose) делает с «Назад» телефона.
// Окон может быть два (вопрос про прошлый звонок и окно следующего) — Esc
// получает то, что открыто последним.

/** Esc в поле — его собственный: свернуть выпадающий список номеров или
 * устройств, закрыть строку поиска (Ctrl+K — она лежит выше окна звонка).
 * Те же теги пропускает и карточка кандидата. */
export function escInField(tag: string | null | undefined): boolean {
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Кому из открытых окон достаётся нажатие: последнему. null — клавиша не
 * наша (не Esc, окон нет, фокус в поле) и идёт дальше как обычно. */
export function escOwner<T>(layers: readonly T[], key: string, tag: string | null | undefined): T | null {
  if (key !== "Escape" || escInField(tag)) return null;
  return layers[layers.length - 1] ?? null;
}

interface Layer {
  onEsc: () => void;
}

const layers: Layer[] = [];

function onKey(e: KeyboardEvent) {
  const top = escOwner(layers, e.key, (e.target as HTMLElement | null)?.tagName);
  if (!top) return;
  e.stopPropagation();
  top.onEsc();
}

/** Пока компонент смонтирован (или пока `open`), Esc зовёт `onEsc` и не
 * доходит до слоёв под окном. */
export function useEscClose(onEsc: () => void, open = true) {
  const ref = useRef(onEsc);
  ref.current = onEsc;
  useEffect(() => {
    if (!open) return;
    const layer: Layer = { onEsc: () => ref.current() };
    if (layers.length === 0) window.addEventListener("keydown", onKey, true);
    layers.push(layer);
    return () => {
      const i = layers.indexOf(layer);
      if (i >= 0) layers.splice(i, 1);
      if (layers.length === 0) window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
}
